// Procedencia del registro (funnel de captación v2, CC-CONSOLE#1520).
//
// La landing (o un enlace corto / QR) manda a la gente a
// `/register?utm_source=…&ref=…&channel=…&name=…&email=…&phone=…&company=…&intent=…`.
// Aquí se captura todo eso al llegar, se guarda para el resto del flujo
// (registro → «ya tengo cuenta» → login → empresa) y se quita de la URL:
// `name`, `email` y `phone` son datos de una persona y no deben quedarse en
// la barra de direcciones ni en los eventos de analítica.
//
// Mismo patrón que utils/demoInvite: sessionStorage como almacén principal.
// La ATRIBUCIÓN tiene además un respaldo en localStorage con vencimiento de
// 1 hora (una pestaña nueva abierta desde el correo de verificación no
// comparte sessionStorage); el PRELLENADO (nombre, correo, teléfono) no: son
// datos de una persona y no sobreviven a la pestaña. Primer toque gana: si ya
// había una atribución guardada, la de la URL sólo rellena lo que falte.
//
// Sin dependencias a propósito (ni React, ni router, ni analítica).

export const CHANNELS = ["landing", "whatsapp", "meta_ads", "qr", "pos", "flyer", "app_store", "referral", "other"] as const;
export type Channel = (typeof CHANNELS)[number];

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Código del enlace corto / ctwa_clid. */
  ref?: string;
  referrer?: string;
  landingPath?: string;
  /** ISO. */
  firstSeenAt?: string;
  channel: Channel;
  /**
   * `distinct_id` de PostHog del visitante en la landing (`?ph_did=`,
   * CC-CONSOLE#1538): con él la consola continúa a la misma persona y el API
   * une la cuenta nueva con lo que hizo antes de registrarse.
   */
  ph_distinct_id?: string;
}

export interface SignupPrefill {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  /**
   * Con qué llegó: qué quiere probar según el formulario «Crear cuenta gratis»
   * (`app`, `gps-propio`, `gps-wayak`; es lo que el backend guarda como
   * `signupIntent`) o la opción del menú del sitio (`demo`, `piloto`,
   * `cuenta-gratis`, `comprar`, `vincular`; sólo informativa).
   */
  intent?: string;
  /**
   * Caso de uso con el que llegó de la landing (`?use_case=`, CC-CONSOLE
   * #1614): `transportista`, `freight-forwarder`, `monitoreo-dedicado` o
   * `pyme-flotilla`. El registro sólo lo usa si es uno de la lista.
   */
  use_case?: string;
}

const ATTRIBUTION_KEY = "signupAttribution";
const PREFILL_KEY = "signupPrefill";
const BACKUP_TTL_MS = 60 * 60 * 1000;

/** Parámetros de la URL que describen de dónde viene la persona. */
export const ATTRIBUTION_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "ref", "channel"] as const;
/** Identidad de PostHog que manda la landing (`ph_did`) y su sesión (`ph_sid`). */
export const IDENTITY_PARAMS = ["ph_did", "ph_sid"] as const;
// Sólo el formato que genera posthog-js (UUID, v7 hoy): el mismo que aceptan
// la landing y el API. Un uid, un `lead:<sha>` o una cadena cualquiera no
// valen: nadie puede colgarse de la historia de otra persona con un enlace.
const DISTINCT_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** ¿Es un id anónimo de posthog-js (UUID)? */
export const isPostHogClientId = (value: unknown): value is string =>
  typeof value === "string" && DISTINCT_ID_RE.test(value.trim());

/** El `ph_did` válido de una query string, o undefined. */
export const posthogIdFromSearch = (search: string): string | undefined => {
  const value = new URLSearchParams(search).get("ph_did")?.trim();
  return value && isPostHogClientId(value) ? value.toLowerCase() : undefined;
};

/** Parámetros de la URL que prellenan el formulario. */
export const PREFILL_PARAMS = ["name", "email", "phone", "company", "intent", "use_case"] as const;

const MAX = 200;

// Respaldo en memoria si no hay ningún almacenamiento (modo privado estricto).
let memoryAttribution: Attribution | null = null;
let memoryPrefill: SignupPrefill | null = null;

const session = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};
const local = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const clean = (value: string | null | undefined, max = MAX): string | undefined => {
  if (typeof value !== "string") return undefined;
  const s = value.replace(/\s+/g, " ").trim();
  return s ? s.slice(0, max) : undefined;
};

/**
 * Canal a partir de los UTM / `ref` cuando la URL no lo dice. Mismas reglas
 * que la landing y el API; si nada aplica, es tráfico directo de la landing.
 */
export const inferChannel = (attr: Partial<Attribution>): Channel => {
  const source = (attr.utm_source || "").toLowerCase();
  const medium = (attr.utm_medium || "").toLowerCase();
  if (attr.ref && !medium) return "qr";
  if (["cpc", "paid", "ads", "meta"].includes(medium) || ["facebook", "instagram", "meta"].includes(source)) return "meta_ads";
  if (medium === "chatbot" || source === "whatsapp") return "whatsapp";
  if (["flyer", "folleto", "print"].includes(medium)) return "flyer";
  if (medium === "pos") return "pos";
  if (["app_store", "play_store", "appstore"].includes(source)) return "app_store";
  if (medium === "referral") return "referral";
  return "landing";
};

/** Atribución leída de una query string, o null si no trae ninguno de sus parámetros. */
export const attributionFromSearch = (search: string): Attribution | null => {
  const params = new URLSearchParams(search);
  const partial: Partial<Attribution> = {};
  let any = false;
  for (const key of ATTRIBUTION_PARAMS) {
    const value = clean(params.get(key));
    if (!value) continue;
    any = true;
    if (key === "channel") continue;
    partial[key] = value;
  }
  const phId = posthogIdFromSearch(search);
  if (phId) {
    any = true;
    partial.ph_distinct_id = phId;
  }
  if (!any) return null;
  const declared = clean(params.get("channel"), 40)?.toLowerCase();
  const channel: Channel = declared
    ? ((CHANNELS as readonly string[]).includes(declared) ? (declared as Channel) : "other")
    : inferChannel(partial);
  const out: Attribution = { ...partial, channel };
  try {
    const referrer = clean(document.referrer, 2048);
    if (referrer) out.referrer = referrer;
    out.landingPath = clean(`${window.location.pathname}${window.location.hash}`, 512) || "/register";
  } catch {
    /* sin documento (pruebas): sin referrer */
  }
  out.firstSeenAt = new Date().toISOString();
  return out;
};

/** Prellenado leído de una query string, o null si no trae ninguno de sus parámetros. */
export const prefillFromSearch = (search: string): SignupPrefill | null => {
  const params = new URLSearchParams(search);
  const out: SignupPrefill = {};
  for (const key of PREFILL_PARAMS) {
    const short = key === "intent" || key === "use_case";
    const value = clean(params.get(key), short ? 60 : MAX);
    if (value) out[key] = short ? value.toLowerCase() : value;
  }
  return Object.keys(out).length ? out : null;
};

const readJson = <T>(storage: Storage | null, key: string): T | null => {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const readBackup = <T>(key: string): T | null => {
  const entry = readJson<{ value: T; expiresAt: number }>(local(), key);
  if (!entry || typeof entry.expiresAt !== "number") return null;
  if (entry.expiresAt < Date.now()) {
    try {
      local()?.removeItem(key);
    } catch {
      /* nada */
    }
    return null;
  }
  return entry.value ?? null;
};

const save = (key: string, value: unknown, { backup }: { backup: boolean }): void => {
  try {
    session()?.setItem(key, JSON.stringify(value));
  } catch {
    /* sin sessionStorage: queda el respaldo o la memoria */
  }
  if (!backup) return;
  try {
    local()?.setItem(key, JSON.stringify({ value, expiresAt: Date.now() + BACKUP_TTL_MS }));
  } catch {
    /* sin localStorage: queda la memoria */
  }
};

/** Primer toque gana: `first` manda y de `next` sólo entra lo que falte. */
const mergeFirstWins = <T extends object>(first: T | null, next: T | null): T | null => {
  if (!first) return next;
  if (!next) return first;
  const out = { ...first } as Record<string, unknown>;
  for (const [key, value] of Object.entries(next)) {
    if (out[key] === undefined && value !== undefined) out[key] = value;
  }
  return out as T;
};

/** La atribución vigente (sessionStorage → respaldo 1 h → memoria), o null. */
export const getAttribution = (): Attribution | null => {
  const stored = readJson<Attribution>(session(), ATTRIBUTION_KEY) ?? readBackup<Attribution>(ATTRIBUTION_KEY);
  if (stored && typeof stored === "object" && typeof stored.channel === "string") return stored;
  return memoryAttribution;
};

/** El prellenado vigente (sólo sessionStorage → memoria; sin respaldo), o null. */
export const getPrefill = (): SignupPrefill | null => {
  const stored = readJson<SignupPrefill>(session(), PREFILL_KEY);
  if (stored && typeof stored === "object" && !Array.isArray(stored)) return stored;
  return memoryPrefill;
};

/**
 * Guarda lo que traiga la URL (atribución y prellenado) para el resto del
 * flujo. Devuelve lo que quedó vigente; `captured` dice si la URL traía algo
 * (para saber si hay que limpiarla).
 */
export const captureAttribution = (
  search: string = window.location.search,
): { attribution: Attribution | null; prefill: SignupPrefill | null; captured: boolean } => {
  const fromUrl = attributionFromSearch(search);
  const prefillFromUrl = prefillFromSearch(search);
  if (fromUrl) {
    memoryAttribution = mergeFirstWins(getAttribution(), fromUrl);
    save(ATTRIBUTION_KEY, memoryAttribution, { backup: true });
  }
  if (prefillFromUrl) {
    // El prellenado sí lo pisa la URL nueva: es lo que la persona acaba de
    // escribir en la landing. Sin respaldo en localStorage (datos personales).
    memoryPrefill = { ...(getPrefill() || {}), ...prefillFromUrl };
    save(PREFILL_KEY, memoryPrefill, { backup: false });
  }
  return { attribution: getAttribution(), prefill: getPrefill(), captured: Boolean(fromUrl || prefillFromUrl) };
};

/** Quita de una query string los parámetros que ya se capturaron (deja `plan`, `billing`, …). */
export const stripAttributionParams = (params: URLSearchParams): boolean => {
  let changed = false;
  for (const key of [...ATTRIBUTION_PARAMS, ...PREFILL_PARAMS, ...IDENTITY_PARAMS]) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  return changed;
};

/** Ya se usó (la empresa quedó creada): que no se reenvíe ni prellene otra vez. */
export const clearAttribution = (): void => {
  memoryAttribution = null;
  memoryPrefill = null;
  for (const key of [ATTRIBUTION_KEY, PREFILL_KEY]) {
    try {
      session()?.removeItem(key);
    } catch {
      /* nada que limpiar */
    }
    try {
      local()?.removeItem(key);
    } catch {
      /* nada que limpiar */
    }
  }
};

/** «Ana López Ruiz» → { firstName: "Ana", lastName: "López Ruiz" }. */
export const splitFullName = (name: string | undefined): { firstName: string; lastName: string } => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};
